// components/PageContainer.jsx
import React from 'react';
import { Card, Typography, Space, Button, Row, Col, Divider, Badge, Tooltip } from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  FilterOutlined,
  ExportOutlined,
  SettingOutlined,
  QuestionCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const PageContainer = ({
  // Título y contenido
  title,
  subtitle,
  icon,
  children,
  extra,
  
  // Estados
  loading = false,
  badgeCount,
  badgeStatus = 'default',
  
  // Acciones
  actions = [],
  onRefresh,
  onCreate,
  onExport,
  onSettings,
  
  // Estilos
  cardStyle = {},
  bodyStyle = {},
  headerStyle = {},
  
  // Opciones de visualización
  showDivider = false,
  showHeader = true,
  fullHeight = false,
  noPadding = false
}) => {
  
  // Acciones por defecto
  const defaultActions = [
    onRefresh && {
      key: 'refresh',
      label: 'Actualizar',
      icon: <ReloadOutlined />,
      onClick: onRefresh,
      type: 'default',
      tooltip: 'Actualizar datos'
    },
    onCreate && {
      key: 'create',
      label: 'Nuevo',
      icon: <PlusOutlined />,
      onClick: onCreate,
      type: 'primary',
      tooltip: 'Crear nuevo registro'
    },
    onExport && {
      key: 'export',
      label: 'Exportar',
      icon: <ExportOutlined />,
      onClick: onExport,
      tooltip: 'Exportar datos'
    }
  ].filter(Boolean);

  // Combinar acciones
  const allActions = [...defaultActions, ...actions];

  // Renderizar título
  const renderTitle = () => (
    <Space direction="vertical" size={2} style={{ lineHeight: 1.2 }}>
      <Space align="center" size="middle">
        {icon && React.cloneElement(icon, { 
          style: { fontSize: '22px', color: '#1890ff' } 
        })}
        <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
          {title}
          {badgeCount !== undefined && (
            <Badge 
              count={badgeCount} 
              status={badgeStatus}
              style={{ 
                marginLeft: 8,
                marginBottom: 4 
              }}
              size="small"
            />
          )}
        </Title>
        {extra && (
          <div style={{ marginLeft: 'auto' }}>
            {extra}
          </div>
        )}
      </Space>
      {subtitle && (
        <Text type="secondary" style={{ 
          fontSize: '13px', 
          marginLeft: icon ? 32 : 0,
          fontWeight: 400 
        }}>
          {subtitle}
        </Text>
      )}
    </Space>
  );

  // Renderizar acciones
  const renderActions = () => {
    if (allActions.length === 0) return null;

    return (
      <Space wrap size="middle">
        {allActions.map((action, index) => {
          if (action.type === 'divider') {
            return <Divider key={`divider-${index}`} type="vertical" />;
          }

          if (action.type === 'custom') {
            return <div key={`custom-${index}`}>{action.content}</div>;
          }

          const btn = (
            <Button
              key={action.key || index}
              type={action.type || 'default'}
              icon={action.icon}
              onClick={action.onClick}
              danger={action.danger}
              disabled={action.disabled}
              loading={action.loading}
              size={action.size || 'middle'}
              style={action.style}
            >
              {action.label}
            </Button>
          );

          return action.tooltip ? (
            <Tooltip key={action.key || index} title={action.tooltip}>
              {btn}
            </Tooltip>
          ) : btn;
        })}
      </Space>
    );
  };

  return (
    <Card
      loading={loading}
      title={showHeader && renderTitle()}
      extra={showHeader && renderActions()}
      style={{
        borderRadius: '10px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        border: '1px solid #f0f0f0',
        height: fullHeight ? 'calc(100vh - 180px)' : 'auto',
        minHeight: fullHeight ? '500px' : 'auto',
        backgroundColor: '#fff',
        overflow: 'hidden',
        ...cardStyle
      }}
      bodyStyle={{
        padding: noPadding ? 0 : 24,
        ...bodyStyle
      }}
      headStyle={{
        borderBottom: showDivider ? '1px solid #f0f0f0' : 'none',
        padding: '16px 24px',
        backgroundColor: '#fafafa',
        ...headerStyle
      }}
    >
      {showDivider && (
        <Divider style={{ margin: '0 0 20px 0' }} />
      )}
      
      <div style={{
        height: fullHeight ? 'calc(100% - 60px)' : 'auto',
        overflow: fullHeight ? 'auto' : 'visible'
      }}>
        {children}
      </div>
    </Card>
  );
};

export default PageContainer;